import { useState } from 'react'
import { toast } from 'sonner'
import { Modal } from './Modal.js'
import { AdminApiError } from '../adminApi.js'
import {
  useSuspendTenant,
  useUnsuspendTenant,
} from '../hooks/useTenantMutations.js'

interface SuspendModalProps {
  tenantId: string
  tenantName: string
  /** 'suspend' shows the reason form; 'unsuspend' is a plain confirm. */
  mode: 'suspend' | 'unsuspend'
  onClose: () => void
}

/**
 * Suspend / unsuspend confirmation. Suspending requires a reason (audit-logged
 * and surfaced in the tenant's activity). Unsuspending is a one-tap confirm.
 */
export function SuspendModal({
  tenantId,
  tenantName,
  mode,
  onClose,
}: SuspendModalProps) {
  const suspend = useSuspendTenant(tenantId)
  const unsuspend = useUnsuspendTenant(tenantId)
  const [reason, setReason] = useState('')

  function doSuspend() {
    if (reason.trim().length === 0) {
      toast.error('A suspension reason is required.')
      return
    }
    suspend.mutate(
      { reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${tenantName} suspended.`)
          onClose()
        },
        onError: (err) =>
          toast.error(
            err instanceof AdminApiError
              ? err.userMessage
              : 'Could not suspend tenant.',
          ),
      },
    )
  }

  function doUnsuspend() {
    unsuspend.mutate(undefined, {
      onSuccess: () => {
        toast.success(`${tenantName} unsuspended.`)
        onClose()
      },
      onError: (err) =>
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not unsuspend tenant.',
        ),
    })
  }

  if (mode === 'unsuspend') {
    return (
      <Modal title="Unsuspend tenant" onClose={onClose} maxWidth={420}>
        <p style={{ marginTop: 0 }}>
          Restore <strong>{tenantName}</strong> to active? Owner and staff will
          be able to make changes again.
        </p>
        <div className="admin-form-actions">
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={doUnsuspend}
            disabled={unsuspend.isPending}
          >
            {unsuspend.isPending ? 'Working…' : 'Unsuspend'}
          </button>
          <button type="button" className="admin-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="Suspend tenant" onClose={onClose} maxWidth={460}>
      <p style={{ marginTop: 0 }}>
        Suspending <strong>{tenantName}</strong> blocks all mutating actions
        for its owner and staff. Reads still work; broadcasts pause.
      </p>
      <div className="admin-field">
        <label className="admin-label" htmlFor="suspend-reason">
          Reason
        </label>
        <textarea
          id="suspend-reason"
          className="admin-textarea"
          placeholder="Why is this tenant being suspended?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-btn admin-btn-danger"
          onClick={doSuspend}
          disabled={suspend.isPending}
        >
          {suspend.isPending ? 'Suspending…' : 'Suspend tenant'}
        </button>
        <button type="button" className="admin-btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  )
}
